import { useEffect, useRef } from 'react';
import '../styles/ConfirmDialog.css';

/**
 * Modal confirm dialog. The parent owns the action and the busy state.
 * Renders nothing while `open` is false. A fixed backdrop covers the page;
 * a press on it, or Escape inside the dialog, calls onCancel. Tab wraps
 * between the two buttons so focus stays inside. When `danger` is set the
 * cancel button takes the initial focus so Enter does not run the action;
 * otherwise the confirm button does.
 */
export default function ConfirmDialog({
    open,
    title,
    children,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onConfirm,
    onCancel,
    busy = false,
    danger = false,
}) {
    const cancelRef = useRef(null);
    const confirmRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const target = danger ? cancelRef.current : confirmRef.current;
        target?.focus();
    }, [open, danger]);

    if (!open) return null;

    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
            return;
        }
        if (e.key !== 'Tab' || busy) return;
        // The two buttons are the only focusable elements, so wrapping between
        // them is the whole trap. Tab between them stays with the browser.
        const first = cancelRef.current;
        const last = confirmRef.current;
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    return (
        <div className="confirm-backdrop" data-testid="confirm-backdrop" onMouseDown={onCancel}>
            <div
                className="confirm-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-dialog-title"
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={onKeyDown}
            >
                <h3 id="confirm-dialog-title">{title}</h3>
                <div className="confirm-dialog-copy">{children}</div>
                <div className="confirm-dialog-actions">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onCancel}
                        disabled={busy}
                        ref={cancelRef}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
                        onClick={onConfirm}
                        disabled={busy}
                        ref={confirmRef}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
